/* eslint-disable import/no-cycle */
/* eslint-disable react/require-default-props */
import React, { Children, isValidElement, type ReactNode, useCallback } from 'react'

import { documentToReactComponents, NodeRenderer, type Options } from '@contentful/rich-text-react-renderer'
import {
  BLOCKS,
  INLINES,
  type Block,
  type Document,
  type Heading1,
  type Heading2,
  type Heading3,
  type Heading4,
  type Heading5,
  type Heading6,
  type Hyperlink,
  type Inline,
  type ListItem as ListItemType,
  type Node,
  type OrderedList as OrderedListType,
  type Paragraph,
  type Table,
  type TableCell,
  type TableHeaderCell,
  type TableRow,
  type Text,
  type UnorderedList as UnorderedListType,
} from '@contentful/rich-text-types'
import { Asset } from 'contentful'
import {
  isTypeSeo,
  isTypeHelpArticle,
  isTypeComponentLink,
  TypeComponentQuote,
  TypeComponentCallout,
  TypeComponentCode,
  TypeComponentHubSpotForm,
  TypeComponentIcon,
  TypeResourceImage,
  TypeComponentTabs,
  TypeComponentVideo,
  TypeComponentRichText,
  TypeSectionCta,
  TypeSectionTileLinks,
  TypeSectionAccordion,
  TypeComponentAccordionItem,
} from '@/types/ctf'

import Link from '../link-component.js'
import HeadingWithAnchor from '../heading-with-anchor.tsx'
import BwTable from './table/bw-table.tsx'
import colors from '@/constants/colors'
// eslint-disable-next-line import/no-self-import
import RichTextComponent from './rich-text.tsx'
import Callout from './callout.tsx'
import Code from './code/code.tsx'
import SectionCta from '@/components/section/cta'
import HubSpotForm from './hubspot-form.tsx'
import Icon from './icon-font.tsx'
import Image from './image.tsx'
import Quote from './quote.tsx'
import Tabs from './tabs.tsx'
import Video from './video.tsx'
import CtaLink from './cta-link.tsx'
import TileLinks from '../section/tile-links.tsx'
import Accordion from '../section/accordion.tsx'
import AccordionItem from './accordion-item.tsx'
import OverflowIndicator from './overflow-indicator.tsx'
import { cx } from '@/utils/strings.ts'
import { defaultLocale, localizedPath, useLocale, useTranslations } from '@/utils/locale.ts'
import { usePage } from '@inertiajs/react'
import ComparisonTable from '@/components/table.tsx'

type RichTextProps = {
  body: any
  contentWidth?: string
  backgroundColor?: string
  tableColor?: string
  customListIcon?: any
  h1?: (node: Heading1, children: ReactNode) => ReactNode
  h2?: (node: Heading2, children: ReactNode) => ReactNode
  h3?: (node: Heading3, children: ReactNode) => ReactNode
  h4?: (node: Heading4, children: ReactNode) => ReactNode
  h5?: (node: Heading5, children: ReactNode) => ReactNode
  h6?: (node: Heading6, children: ReactNode) => ReactNode
  p?: (node: Paragraph, children: ReactNode) => ReactNode
  blockquote?: (node: Paragraph, children: ReactNode) => ReactNode
  ol?: (node: OrderedListType, children: ReactNode) => ReactNode
  ul?: (node: UnorderedListType, children: ReactNode) => ReactNode
  li?: (node: ListItemType, children: ReactNode) => ReactNode
  table?: (node: Table, children: ReactNode) => ReactNode
  tableCell?: (node: TableCell, children: ReactNode) => ReactNode
  tableHeaderCell?: (node: TableHeaderCell, children: ReactNode) => ReactNode
  tableRow?: (node: TableRow, children: ReactNode) => ReactNode
  a?: (node: Hyperlink, children: ReactNode) => ReactNode
  bold?: (text: ReactNode) => ReactNode
  code?: (text: ReactNode) => ReactNode
  renderCallout?: (entry: TypeComponentCallout<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
  renderCode?: (entry: TypeComponentCode<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
  renderSectionCta?: (entry: TypeSectionCta<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
  renderHubSpotForm?: (entry: TypeComponentHubSpotForm<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
  renderIcon?: (entry: TypeComponentIcon<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
  renderImage?: (asset: Asset<'WITHOUT_UNRESOLVABLE_LINKS', any>, node: Node) => ReactNode
  renderComponentImage?: (entry: TypeResourceImage<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
  renderQuote?: (entry: TypeComponentQuote<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
  renderTabs?: (entry: TypeComponentTabs<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
  renderVideo?: (entry: TypeComponentVideo<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
  renderRichText?: (entry: TypeComponentRichText<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
  renderComponentTable?: (entry: TypeComponentTable<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
  renderTileLinks?: (entry: TypeSectionTileLinks<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
  renderAccordion?: (entry: TypeSectionAccordion<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
  renderAccordionItem?: (entry: TypeComponentAccordionItem<'WITHOUT_UNRESOLVABLE_LINKS'>, node: Node) => ReactNode
}

export default function RichText({
  contentWidth = 'narrow',
  tableColor = 'gray',
  backgroundColor = colors.white,
  body,
  customListIcon,
  h1 = (node, children) => <h1>{children}</h1>,
  h2 = (node, children) => {
    const title = Array.isArray(children) ? children?.[0]?.[1] || children?.[0]?.props?.children[1] : null
    return <HeadingWithAnchor type="h2" title={title} />
  },
  h3 = (node, children) => {
    const title = Array.isArray(children) ? children?.[0]?.[1] || children?.[0]?.props?.children[1] : null
    return <HeadingWithAnchor type="h3" title={title} />
  },
  h4 = (node, children) => <h4>{children}</h4>,
  h5 = (node, children) => <h5>{children}</h5>,
  h6 = (node, children) => <h6>{children}</h6>,
  p = (node, children) => <p>{children}</p>,
  blockquote = (node, children) => (
    <blockquote className="font-medium leading-normal text-left border-l-8 border-primaryBlue my-8 py-4 pr-0 pl-6 [*]:text-xl [&_p:last-child]:m-0">
      {children}
    </blockquote>
  ),
  ol = (node, children) => <ol className="mb-6 ml-5 pl-0">{children}</ol>,
  ul = (node, children) => <ul className={`mb-6 pl-0 ${customListIcon ? 'ml-8' : 'ml-5'}`}>{children}</ul>,
  li = (node, children) => (
    <li className={cx(customListIcon && 'list-none', 'relative mb-2')}>
      {customListIcon && <Icon {...customListIcon.fields} className="absolute top-0 -left-7" />}
      {children}
    </li>
  ),
  table = (node, children) => {
    let thead = null
    const trChildren: any = []
    Children.forEach(children, (child) => {
      if (isValidElement(child)) {
        if (child.type === 'thead') thead = child
        else if (child.type === 'tr') trChildren.push(child)
      }
    })

    return (
      <div className={cx('table-container', contentWidth === 'wide' && `pt-5 lg:pt-8`)}>
        <OverflowIndicator className="mb-5 lg:mb-10 overflow-x-auto [*>p]:mb-0" disableOverflowY threshold={24}>
          <BwTable tableColor={tableColor} contentWidth={contentWidth}>
            {thead}
            {trChildren.length > 0 && <tbody>{trChildren}</tbody>}
          </BwTable>
        </OverflowIndicator>
      </div>
    )
  },
  tableCell = (node) => (
    <td className="relative [&_code]:break-all">
      {node.content.map((child, index) => (
        <RichTextComponent body={child} key={`td-${index}`} />
      ))}
    </td>
  ),
  tableHeaderCell = (node) => (
    <th>
      <RichTextComponent body={node.content[0]} />
    </th>
  ),
  tableRow = (node, children) => {
    const childType = node.content[0].nodeType
    if (BLOCKS.TABLE_HEADER_CELL === childType)
      return (
        <thead>
          <tr>{children}</tr>
        </thead>
      )
    if (BLOCKS.TABLE_CELL === childType) return <tr>{children}</tr>
  },
  a = (node, children) => {
    return (
      <Link
        to={node.data.uri}
        className={cx(
          'underline transition-colors',
          backgroundColor === colors.white && 'text-primaryBlue hover:text-bw-deep-blue',
          backgroundColor === colors.lightGray && 'text-primaryBlue hover:text-bw-deep-blue',
          backgroundColor === colors.primaryBlue && 'text-white hover:text-bw-deep-blue',
          backgroundColor === colors.indigoBlue && 'text-primaryBlue hover:text-primaryBlue',
          'dark:text-primaryBlue dark:hover:text-white'
        )}
      >
        {children}
      </Link>
    )
  },
  bold = (text) => <strong>{text}</strong>,
  code = (text) => {
    const { t } = useTranslations()
    const handleCopy = (event: React.MouseEvent) => {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        // Handle array with false values - filter them out
        let textToCopy = ''
        if (Array.isArray(text)) {
          textToCopy = text.filter((item) => item !== false && item !== null && item !== undefined).join('')
        } else if (typeof text === 'string') {
          textToCopy = text
        } else {
          textToCopy = String(text)
        }

        navigator.clipboard.writeText(textToCopy)

        //make all the text selected for a sec
        const selection = window.getSelection()
        const range = document.createRange()
        const codeElement = event.currentTarget as HTMLElement
        range.selectNodeContents(codeElement)
        selection?.removeAllRanges()
        selection?.addRange(range)
        setTimeout(() => {
          selection?.removeAllRanges()
        }, 1000)

        // Copied! tooltip shows for a mo
        const tooltip = document.createElement('span')
        tooltip.textContent = t('Copied!')
        tooltip.className =
          'fixed bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-90 pointer-events-none whitespace-nowrap z-50'
        tooltip.style.left = `${event.clientX - 25}px` // Center the tooltip on the click
        tooltip.style.top = `${event.clientY - 35}px` // Position above the click

        document.body.appendChild(tooltip)

        setTimeout(() => {
          if (tooltip.parentNode) {
            tooltip.parentNode.removeChild(tooltip)
          }
        }, 1500)
      }
    }

    return (
      <code
        className="text-code break-words rounded-sm border border-secondary-200 hover:border-secondary-400 dark:border-secondary-700 dark:hover:border-secondary-600 px-2 cursor-default py-[1px] leading-tight dark:selection:bg-secondary-600"
        onClick={handleCopy}
        title={t('Click to copy')}
      >
        {text}
      </code>
    )
  },
  renderCallout = (entry) => (
    <Callout contentful_id={entry.sys.id} {...entry.fields}>
      <RichTextComponent body={entry.fields.body} />
    </Callout>
  ),
  renderCode = (entry) => <Code {...entry?.fields} />,
  renderSectionCta = (entry) => <SectionCta {...entry?.fields} className="!px-0" />,
  renderHubSpotForm = (entry) => <HubSpotForm {...entry?.fields} />,
  renderIcon = (entry) => <Icon className="rich-icon" {...entry?.fields} />,
  renderImage = (asset) => <Image image={asset} showCaption />,
  renderComponentTable = (entry) => <ComparisonTable {...entry?.fields} />,
  renderComponentImage = (entry) => <Image {...entry?.fields} />,
  renderTabs = (entry) => <Tabs groupId={entry?.sys.id} {...entry?.fields} />,
  renderQuote = (entry) => <Quote {...entry?.fields} />,
  renderVideo = (entry) => (
    <div className="mb-8">
      <Video {...entry} />
    </div>
  ),
  renderRichText = (entry) => <RichText {...entry?.fields} />,
  renderTileLinks = (entry) => <TileLinks {...entry?.fields} />,
  renderAccordion = (entry) => <Accordion {...entry?.fields} />,
  renderAccordionItem = (entry) => <AccordionItem {...entry?.fields} nonControlled />,
}: RichTextProps) {
  if (typeof body !== 'object' || !body) return null
  const page = usePage()
  const optionsObj: Options = {
    renderNode: {
      [BLOCKS.HEADING_1]: useCallback(h1 as NodeRenderer, []),
      [BLOCKS.HEADING_2]: useCallback(h2 as NodeRenderer, []),
      [BLOCKS.HEADING_3]: useCallback(h3 as NodeRenderer, []),
      [BLOCKS.HEADING_4]: useCallback(h4 as NodeRenderer, []),
      [BLOCKS.HEADING_5]: useCallback(h5 as NodeRenderer, []),
      [BLOCKS.HEADING_6]: useCallback(h6 as NodeRenderer, []),
      [BLOCKS.PARAGRAPH]: useCallback(p as NodeRenderer, []),
      [BLOCKS.QUOTE]: useCallback(blockquote as NodeRenderer, []),
      [BLOCKS.OL_LIST]: useCallback(ol as NodeRenderer, []),
      [BLOCKS.UL_LIST]: useCallback(ul as NodeRenderer, []),
      [BLOCKS.LIST_ITEM]: useCallback(li as NodeRenderer, []),
      [BLOCKS.TABLE]: useCallback(table as NodeRenderer, []),
      [BLOCKS.TABLE_CELL]: useCallback(tableCell as NodeRenderer, []),
      [BLOCKS.TABLE_HEADER_CELL]: useCallback(tableHeaderCell as NodeRenderer, []),
      [BLOCKS.TABLE_ROW]: useCallback(tableRow as NodeRenderer, []),
      [BLOCKS.EMBEDDED_ENTRY]: useCallback((node) => {
        const entry = node?.data?.target
        const type = entry?.sys?.contentType?.sys?.id
        if (type === 'componentCallout') return renderCallout(entry, node)
        if (type === 'componentCode') return renderCode(entry, node)
        if (type === 'sectionCta') return renderSectionCta(entry, node)
        if (type === 'componentHubSpotForm') return renderHubSpotForm(entry, node)
        if (type === 'resourceImage') return renderComponentImage(entry, node)
        if (type === 'componentQuote') return renderQuote(entry, node)
        if (type === 'componentTabs') return renderTabs(entry, node)
        if (type === 'componentVideo') return renderVideo(entry, node)
        if (type === 'componentIcon') return renderIcon(entry, node)
        if (type === 'componentRichText') return renderRichText(entry, node)
        if (type === 'componentTable') return renderComponentTable(entry, node)
        if (type === 'componentCtaLink') {
          return (
            <CtaLink {...entry.fields} to={entry.fields?.link?.fields?.url}>
              {entry.fields.label}
            </CtaLink>
          )
        }
        if (type === 'sectionTileLinks') return renderTileLinks(entry, node)
        if (type === 'sectionAccordion') return renderAccordion(entry, node)
        if (type === 'componentAccordionItem') return renderAccordionItem(entry, node)
      }, []),
      [BLOCKS.EMBEDDED_ASSET]: useCallback((node) => {
        if (!node.data?.target) return null
        return renderImage(node.data?.target, node)
      }, []),
      [INLINES.ASSET_HYPERLINK]: useCallback((node) => {
        return (
          <Link
            title={`${(node.content[0] as Text).value}`}
            to={node?.data?.target?.fields?.file?.url}
            className={cx(
              'underline transition-colors',
              backgroundColor === colors.white && 'text-primaryBlue hover:text-bw-deep-blue',
              backgroundColor === colors.lightGray && 'text-primaryBlue hover:text-bw-deep-blue',
              backgroundColor === colors.primaryBlue && 'text-white hover:text-bw-deep-blue',
              backgroundColor === colors.indigoBlue && 'text-primaryBlue hover:text-primaryBlue',
              'dark:text-primaryBlue dark:hover:text-white'
            )}
          >
            <RichTextComponent body={node.content[0]} />
          </Link>
        )
      }, []),
      [INLINES.EMBEDDED_ENTRY]: useCallback((node) => {
        const entry = node?.data?.target
        const type = entry?.sys?.contentType?.sys?.id
        if (type === 'componentIcon') return renderIcon(entry, node)
        if (type === 'resourceImage') return renderComponentImage(entry, node)
        if (type === 'componentCtaLink') {
          return (
            <CtaLink {...entry.fields} to={entry.fields?.link?.fields?.url}>
              {entry.fields.label}
            </CtaLink>
          )
        }
      }, []),
      [INLINES.HYPERLINK]: useCallback(a as NodeRenderer, []),
      [INLINES.ENTRY_HYPERLINK]: useCallback((node) => {
        let path
        const entry = node?.data?.target
        const isEntryLink = entry?.sys?.type === 'Link'
        if (entry && !isEntryLink) {
          if (isTypeSeo<'WITHOUT_UNRESOLVABLE_LINKS', any>(entry)) {
            path = entry.fields.path
          } else if (isTypeHelpArticle<'WITHOUT_UNRESOLVABLE_LINKS', any>(entry)) {
            path = `/help/${entry.fields.slug}/${(node.content?.[0] as any)?.hash || ''}`
          } else if (isTypeComponentLink<'WITHOUT_UNRESOLVABLE_LINKS', any>(entry)) {
            path = entry.fields.url
          } else if (node.nodeType === INLINES.ENTRY_HYPERLINK) {
            path = entry?.fields?.slug || entry?.fields?.url
          }
        }

        const { currentLocale } = useLocale(page.url)

        path = localizedPath(path, currentLocale)

        // localize slugs are not correct atm
        if (
          currentLocale !== defaultLocale &&
          path.includes('#') &&
          !path.includes('#osano') &&
          !path.includes('#consent') &&
          path.startsWith('/') &&
          !path.startsWith('//')
        ) {
          const currentPathname = page.url
          const targetPathname = new URL(`https://bitwarden.com${path}`).toString()

          if (localizedPath(currentPathname, defaultLocale) === localizedPath(targetPathname, defaultLocale)) path = ''
          else path = targetPathname
        }

        if (!path) {
          return <RichTextComponent body={node.content[0]} />
        }
        return (
          <Link
            to={path || ''}
            className={cx(
              'underline transition-colors',
              backgroundColor === colors.white && 'text-primaryBlue hover:text-bw-deep-blue',
              backgroundColor === colors.lightGray && 'text-primaryBlue hover:text-bw-deep-blue',
              backgroundColor === colors.primaryBlue && 'text-white hover:text-bw-deep-blue',
              backgroundColor === colors.indigoBlue && 'text-primaryBlue hover:text-primaryBlue',
              'dark:text-primaryBlue dark:hover:text-white'
            )}
          >
            <RichTextComponent body={node.content[0]} />
          </Link>
        )
      }, []),
    },
    renderMark: {
      ['bold']: useCallback(bold, []),
      ['code']: useCallback(code, []),
    },
    renderText: (text) =>
      text
        ?.split('\n')
        ?.reduce<
          ReactNode[]
        >((children, textSegment, index) => [...children, index > 0 && <br key={index} />, textSegment], []) ?? ' ',
  }

  // here we look for INLINES.ENTRY_HYPERLINK nodes that are succeeded by a #hash-tag
  const toCrawl: Array<Document | Inline | Text | Block> = [body]

  while (toCrawl.length > 0) {
    const node = toCrawl.shift()

    if (node?.nodeType === INLINES.ENTRY_HYPERLINK && toCrawl.length > 0) {
      const [nextNode] = toCrawl
      if (nextNode.nodeType === 'text') {
        const [hash] = nextNode.value.match(/^#[\w-]+/) || []
        if (hash && node.content?.[0]) {
          Object.assign(node.content[0], { hash })
          nextNode.value = nextNode.value.substring(hash.length)
        }
      }
    }

    if (node?.nodeType !== 'text' && node?.content) toCrawl.push(...node.content)
  }
  try {
    return documentToReactComponents(body, optionsObj)
  } catch (e: any) {
    console.error(e)
    console.log(e.message)
  }
}
